import { Button } from "@/components/ui/button";
import { config } from "@/config";
import Sentry from "@/integrations/Sentry";
import { cn } from "@/utils";
import { MessageCircle } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";

export function useFeedback() {
  const [feedback, setFeedback] =
    useState<ReturnType<typeof Sentry.getFeedback>>();

  useEffect(() => {
    try {
      const f = Sentry.getFeedback();
      setFeedback(f);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const launchFeedback = useCallback(async () => {
    try {
      const form = await feedback?.createForm({
        formTitle: "Leave Us Feedback",
        messageLabel: "Feedback",
        messagePlaceholder:
          "Describe your feedback, report a bug, or leave us a comment here",
        submitButtonLabel: "Submit Feedback",
      });
      form?.appendToDom();
      form?.open();
    } catch (e) {
      Sentry.captureException(e);
    }
  }, [feedback]);

  return launchFeedback;
}

const BugReportButton: React.FC<{ className?: string }> = ({ className }) => {
  const launchFeedback = useFeedback();

  if (config.sentry.enabled === false) {
    return false;
  }

  return (
    <Button
      onClick={launchFeedback}
      id="report-a-bug"
      aria-label="Leae Us Feedback"
      variant="default"
      className={cn(
        "gap-0 hover:gap-2 group pointer-events-auto absolute z-99999 bottom-7 right-7 flex items-center text-sm rounded-full py-5 pl-2.5 pr-2.5 transition-all duration-300 ease-in-out",
        className,
      )}
    >
      <MessageCircle className="size-5! pointer-events-none relative top-[-1px]" />
      <div className="pointer-events-none opacity-0 w-0 overflow-hidden group-hover:opacity-100 group-hover:w-[140px] transition-all duration-300">
        Leave Us Feedback
      </div>
    </Button>
  );
};

export default React.memo(BugReportButton);
